/**
 * UI Utilities for TitCock.tv
 *
 * Handles:
 * - Toast notifications
 * - View switching (home/saved/settings)
 * - UI visibility toggling
 * - Animations (bounce, etc.)
 * - Footer indicator
 * - Global horizontal swipe gestures
 */

import { createElement, clamp, formatNumber } from "./utils.js";
import {
  elements,
  viewsState,
  notifyState,
  playerState,
  savedState,
} from "./state.js";

const getTranslateX = (el) => {
  if (!el) return 0;
  const style = window.getComputedStyle(el);
  const transform = style.transform;
  if (transform === "none" || !transform) return 0;

  try {
    const matrix = new DOMMatrix(transform);
    if (!isNaN(matrix.m41)) return matrix.m41;
  } catch (e) { }

  const match = transform.match(/matrix.*\((.+)\)/);
  if (match) {
    const values = match[1].split(/,\s*/);
    if (values.length === 6) return parseFloat(values[4]);
    if (values.length === 16) return parseFloat(values[12]);
  }
  return 0;
};

// View index mapping
const VIEW_ORDER = ["home", "saved", "settings"];
const getViewIndex = (view) => VIEW_ORDER.indexOf(view);
const getViewFromIndex = (index) =>
  VIEW_ORDER[clamp(index, 0, VIEW_ORDER.length - 1)];

// ==========================================================================
// Notifications
// ==========================================================================

/**
 * Show a toast notification
 * @param {string} message - Message to display
 * @param {number} [duration=2000] - Duration in milliseconds
 */
export const notify = (message, duration = 2000) => {
  if (notifyState.timeout) {
    clearTimeout(notifyState.timeout);
  }

  const existed = notifyState.currentNode?.isConnected;
  notifyState.currentNode?.remove();

  const node = createElement("div", "notice", message);
  elements.notifier?.appendChild(node);

  if (existed) {
    node.classList.add("show");
  } else {
    requestAnimationFrame(() => node.classList.add("show"));
  }

  notifyState.currentNode = node;
  notifyState.timeout = setTimeout(() => {
    node.classList.remove("show");
    setTimeout(() => node.remove(), 300);
  }, duration);
};

// ==========================================================================
// Animations
// ==========================================================================

/**
 * Trigger bounce animation on an element
 * @param {HTMLElement} el - Element to animate
 */
export const bounce = (el) => {
  if (!el) return;
  el.classList.remove("bounce");
  void el.offsetWidth; // Force reflow
  el.classList.add("bounce");
};

/**
 * Show a large center heart burst for a new like
 */
export const showLikeBurst = (x = window.innerWidth / 2, y = window.innerHeight / 2) => {
  document.querySelector(".like-burst")?.remove();

  const burst = createElement("div", "like-burst");
  const heart = createElement("img");
  heart.src = "src/svg/liked.svg";
  heart.alt = "";
  heart.setAttribute("aria-hidden", "true");
  burst.style.left = `${x}px`;
  burst.style.top = `${y}px`;

  burst.appendChild(heart);
  document.body.appendChild(burst);

  burst.addEventListener("animationend", () => burst.remove(), { once: true });
};

// ==========================================================================
// Footer Indicator
// ==========================================================================

/**
 * Update active state on footer buttons
 * @param {HTMLElement} targetBtn - Button to mark as active
 */
export const moveFooterIndicator = (targetBtn) => {
  // Remove active class from all footer buttons
  elements.homeBtn?.classList.remove("active");
  elements.savedBtn?.classList.remove("active");
  elements.settingsBtn?.classList.remove("active");

  // Add active class to target button
  targetBtn?.classList.add("active");
};

// ==========================================================================
// UI Visibility
// ==========================================================================

/**
 * Update visibility of header and global controls based on current view
 */
export const updateUIVisibility = () => {
  const { header, globalControls } = elements;
  if (!header || !globalControls) return;

  if (viewsState.currentView === "home" && !savedState.isViewerActive) {
    header.classList.remove("hidden");
    globalControls.classList.remove("hidden");
  } else if (savedState.isViewerActive) {
    header.classList.remove("hidden");
    globalControls.classList.remove("hidden");
  } else {
    header.classList.add("hidden");
    globalControls.classList.add("hidden");
  }
};

/**
 * Hide header and controls
 */
export const hideUI = () => {
  elements.header?.classList.add("hidden");
  elements.globalControls?.classList.add("hidden");
};

/**
 * Show header and controls
 */
export const showUI = () => {
  elements.header?.classList.remove("hidden");
  elements.globalControls?.classList.remove("hidden");
};

// ==========================================================================
// View Switching
// ==========================================================================

/**
 * Switch to a different view (home/saved/settings)
 * @param {string} targetView - "home" | "saved" | "settings"
 * @param {Object} callbacks - Callback functions
 * @param {Function} [callbacks.onBeforeSwitch] - Called before switching
 * @param {Function} [callbacks.onAfterSwitch] - Called after switching
 * @param {boolean} [animated=true] - Whether to animate the transition
 * @returns {Promise<void>}
 */
export const switchToView = async (
  targetView,
  callbacks = {},
  animated = true,
) => {
  const myId = (viewsState.animationId || 0) + 1;
  viewsState.animationId = myId;

  if (targetView === viewsState.currentView) {
    return;
  }

  viewsState.isSwitching = true;

  try {
    // Call before callback (e.g., close saved viewer, return cards)
    if (callbacks.onBeforeSwitch) {
      await callbacks.onBeforeSwitch(viewsState.currentView, targetView);
    }

    viewsState.currentView = targetView;

    // Animate slider
    if (elements.viewsSlider) {
      elements.viewsSlider.style.transition = animated
        ? "transform 320ms cubic-bezier(0.2, 0.8, 0.2, 1)"
        : "none";
      elements.viewsSlider.style.transform = "";
      elements.viewsSlider.classList.remove(
        "view-home",
        "view-saved",
        "view-settings",
      );
      elements.viewsSlider.classList.add(`view-${targetView}`);
    }

    // Move footer indicator
    if (targetView === "home") {
      moveFooterIndicator(elements.homeBtn);
    } else if (targetView === "saved") {
      moveFooterIndicator(elements.savedBtn);
    } else if (targetView === "settings") {
      moveFooterIndicator(elements.settingsBtn);
    }

    updateUIVisibility();

    // Wait for animation
    if (animated) {
      await new Promise((resolve) => setTimeout(resolve, 320));
      if (viewsState.animationId !== myId) return;
    }

    // Call after callback (e.g., render gallery)
    if (callbacks.onAfterSwitch) {
      await callbacks.onAfterSwitch(targetView);
    }
  } finally {
    viewsState.isSwitching = false;
  }
};

/**
 * Get the current view
 * @returns {string} "home" | "saved" | "settings"
 */
export const getCurrentView = () => viewsState.currentView;

/**
 * Check if currently switching views
 * @returns {boolean}
 */
export const isViewSwitching = () => viewsState.isSwitching;

// ==========================================================================
// Mute Icon Update
// ==========================================================================

/**
 * Update the mute button icon based on current state
 */
export const updateMuteIcon = () => {
  if (!elements.muteIcon) return;
  elements.muteIcon.src = playerState.globalMuted
    ? "src/svg/volume-off.svg"
    : "src/svg/volume-on.svg";
};

// ==========================================================================
// Like Icon Update
// ==========================================================================

/**
 * Update the like button icon based on liked state
 * @param {boolean} isLiked - Whether the current video is liked
 */
export const updateLikeIcon = (isLiked) => {
  if (!elements.likeIcon) return;
  elements.likeIcon.src = isLiked ? "src/svg/liked.svg" : "src/svg/like.svg";
};

/**
 * Update the visible like count without changing the scraped cache value.
 * Counts below 1,000 gain one while the current video is locally liked.
 * @param {number|string|null|undefined} likes - Scrape-time like count
 * @param {boolean} isLiked - Whether the current video is locally liked
 */
export const updateLikeCount = (likes, isLiked) => {
  if (!elements.likeCount) return;

  const hasLikes = likes !== undefined && likes !== null && likes !== "";
  if (!hasLikes) {
    elements.likeCount.textContent = "";
    return;
  }

  const scrapedLikes = Number(likes);
  if (!Number.isFinite(scrapedLikes)) {
    elements.likeCount.textContent = "";
    return;
  }

  const visibleLikes = isLiked && scrapedLikes < 1000
    ? scrapedLikes + 1
    : scrapedLikes;
  const text = formatNumber(visibleLikes);
  if (elements.likeCount.textContent !== text) {
    elements.likeCount.textContent = text;
  }
};

// ==========================================================================
// Header Update
// ==========================================================================

/**
 * Update header with video information
 * @param {Object} data - Video data
 * @param {string} [data.slug]
 * @param {string} [data.categoryLabel]
 * @param {string} data.author
 * @param {string} data.title
 * @param {number|string} [data.likes]
 * @param {boolean} data.isLiked
 */
export const updateHeader = (data) => {
  if (elements.categoryFilter && elements.headerCategory) {
    const text = data.categoryLabel || "";
    elements.categoryFilter.hidden = !text;
    if (elements.headerCategory.textContent !== text) {
      elements.headerCategory.textContent = text;
    }
    if (!text) {
      elements.categoryFilter.classList.remove("open");
      elements.headerCategory.setAttribute("aria-expanded", "false");
      elements.categoryMenu?.setAttribute("aria-hidden", "true");
    }
  }
  if (elements.headerAuthor) {
    const text = data.author || "";
    if (elements.headerAuthor.textContent !== text) {
      elements.headerAuthor.textContent = text;
    }
  }
  if (elements.headerTitle) {
    const text = data.title || "";
    if (elements.headerTitle.textContent !== text) {
      elements.headerTitle.textContent = text;
    }
  }
  updateLikeCount(data.likes, data.isLiked || false);
  if (data.slug) {
    const newHash = `#/video/${encodeURIComponent(data.slug)}`;
    if (location.hash !== newHash) {
      history.replaceState(null, "", newHash);
    }
  }
  updateLikeIcon(data.isLiked || false);
};

// ==========================================================================
// Fullscreen
// ==========================================================================

/**
 * Check if fullscreen is supported
 * @returns {boolean}
 */
export const isFullscreenSupported = () => {
  return !!document.documentElement.requestFullscreen;
};

/**
 * Check if currently in fullscreen
 * @returns {boolean}
 */
export const isFullscreen = () => {
  return !!document.fullscreenElement;
};

/**
 * Toggle fullscreen mode
 * @returns {Promise<void>}
 */
export const toggleFullscreen = async () => {
  if (!isFullscreenSupported()) {
    notify("Fullscreen not supported.", 2000);
    return;
  }

  try {
    if (isFullscreen()) {
      await document.exitFullscreen();
    } else {
      await document.documentElement.requestFullscreen();
    }
  } catch {
    notify("Could not toggle fullscreen.", 2000);
  }

  updateFullscreenButton();
};

/**
 * Update fullscreen button title
 */
export const updateFullscreenButton = () => {
  if (!elements.fsBtn) return;
  elements.fsBtn.title = isFullscreen()
    ? "Exit Fullscreen"
    : "Enter Fullscreen";
};

// ==========================================================================
// Initialization
// ==========================================================================

/**
 * Initialize footer (no indicator element needed now)
 */
export const initFooterIndicator = () => {
  // Active state is now handled via CSS classes, no indicator element needed
};

// ==========================================================================
// Global Horizontal Swipe Gestures
// ==========================================================================

/**
 * Get the base X offset for a view
 * @param {string} view
 * @returns {number}
 */
const getViewOffset = (view) => {
  const idx = getViewIndex(view);
  return -idx * window.innerWidth;
};

/**
 * Handle pointer down for global swipe
 * @param {PointerEvent} ev
 */
export const handleGlobalSwipeDown = (ev) => {
  let startX = ev.clientX;

  if (viewsState.isSwitching) {
    viewsState.animationId = (viewsState.animationId || 0) + 1;

    if (elements.viewsSlider) {
      elements.viewsSlider.style.transition = "none";
      const currentX = getTranslateX(elements.viewsSlider);
      const currentViewIdx = getViewIndex(viewsState.currentView);
      const baseOffset = -currentViewIdx * window.innerWidth;

      startX = ev.clientX - currentX + baseOffset;
    }
  }

  viewsState.swipe.active = true;
  viewsState.swipe.didSwipe = false;
  viewsState.swipe.directionLocked = viewsState.isSwitching;
  viewsState.swipe.isHorizontal = viewsState.isSwitching;
  viewsState.swipe.startX = startX;
  viewsState.swipe.startY = ev.clientY;
  viewsState.swipe.currentX = ev.clientX;
  viewsState.swipe.lastX = ev.clientX;
  viewsState.swipe.velocity = 0;
  viewsState.swipe.lastTime = performance.now();
  viewsState.swipe.startView = viewsState.currentView;
};

/**
 * Handle pointer move for global swipe
 * @param {PointerEvent} ev
 */
export const handleGlobalSwipeMove = (ev) => {
  if (!viewsState.swipe.active) return;

  const dx = ev.clientX - viewsState.swipe.startX;
  const dy = ev.clientY - viewsState.swipe.startY;
  const adx = Math.abs(dx);
  const ady = Math.abs(dy);

  if (adx > 5 || ady > 5) {
    viewsState.swipe.didSwipe = true;
  }

  // Determine direction on first significant movement
  if (!viewsState.swipe.directionLocked && (adx > 10 || ady > 10)) {
    viewsState.swipe.directionLocked = true;
    viewsState.swipe.isHorizontal = adx > ady;

    // If horizontal, disable transition for smooth dragging
    if (viewsState.swipe.isHorizontal && elements.viewsSlider) {
      elements.viewsSlider.style.transition = "none";
    }
  }

  // Only handle horizontal swipes
  if (!viewsState.swipe.isHorizontal) return;

  ev.preventDefault();

  // Track velocity
  const now = performance.now();
  const dt = Math.max(1, now - viewsState.swipe.lastTime);
  viewsState.swipe.velocity =
    0.88 * viewsState.swipe.velocity +
    0.12 * ((ev.clientX - viewsState.swipe.lastX) / dt);
  viewsState.swipe.lastX = ev.clientX;
  viewsState.swipe.lastTime = now;

  viewsState.swipe.currentX = ev.clientX;
  const viewIdx = getViewIndex(viewsState.swipe.startView);
  const baseOffset = getViewOffset(viewsState.swipe.startView);

  // Apply rubber band effect at edges
  let adjustedDx = dx;
  if (viewIdx === 0 && dx > 0) {
    // At home, swiping right - rubber band
    adjustedDx = dx * 0.25;
  } else if (viewIdx === VIEW_ORDER.length - 1 && dx < 0) {
    // At settings, swiping left - rubber band
    adjustedDx = dx * 0.25;
  }

  const newOffset = baseOffset + adjustedDx;

  if (elements.viewsSlider) {
    elements.viewsSlider.style.transform = `translateX(${newOffset}px)`;
  }
};

/**
 * Handle pointer up for global swipe
 * @param {PointerEvent} ev
 * @param {Object} callbacks - Callback functions for view switching
 */
export const handleGlobalSwipeUp = async (ev, callbacks = {}) => {
  if (!viewsState.swipe.active) return;

  const wasHorizontal = viewsState.swipe.isHorizontal;

  viewsState.swipe.active = false;
  viewsState.swipe.directionLocked = false;

  setTimeout(() => {
    viewsState.swipe.didSwipe = false;
  }, 50);

  // If it wasn't a horizontal swipe, do nothing
  if (!wasHorizontal) return;

  const dx = viewsState.swipe.currentX - viewsState.swipe.startX;
  const adx = Math.abs(dx);
  const threshold = window.innerWidth * 0.15; // 15% of screen width
  const swift = Math.abs(viewsState.swipe.velocity) > 0.35;
  const minDist = 30;
  const shouldSwitch = adx > threshold || (swift && adx > minDist);
  const currentIdx = getViewIndex(viewsState.swipe.startView);

  let targetView = viewsState.swipe.startView;

  if (shouldSwitch) {
    if (dx < 0 && currentIdx < VIEW_ORDER.length - 1) {
      // Swiped left - go to next view
      targetView = getViewFromIndex(currentIdx + 1);
    } else if (dx > 0 && currentIdx > 0) {
      // Swiped right - go to previous view
      targetView = getViewFromIndex(currentIdx - 1);
    }
  }

  // Animate to target position
  if (elements.viewsSlider) {
    elements.viewsSlider.style.transition =
      "transform 280ms cubic-bezier(0.2, 0.8, 0.2, 1)";
  }

  if (targetView !== viewsState.currentView) {
    // Switch to new view
    await switchToView(targetView, callbacks, true);
    // Clear saved viewer transform after switch/close
    if (elements.savedViewer) elements.savedViewer.style.transform = "";
  } else {
    // Snap back to current view
    if (elements.viewsSlider) {
      elements.viewsSlider.classList.remove(
        "view-home",
        "view-saved",
        "view-settings",
      );
      elements.viewsSlider.classList.add(`view-${viewsState.currentView}`);
      elements.viewsSlider.style.transform = "";
    }
    viewsState.isSwitching = false;
  }
};

/**
 * Cancel global swipe and snap back
 */
export const cancelGlobalSwipe = () => {
  if (!viewsState.swipe.active) return;

  const wasHorizontal = viewsState.swipe.isHorizontal;
  viewsState.swipe.active = false;
  viewsState.swipe.directionLocked = false;
  viewsState.swipe.isHorizontal = false;
  viewsState.isSwitching = false;

  setTimeout(() => {
    viewsState.swipe.didSwipe = false;
  }, 50);

  if (wasHorizontal && elements.viewsSlider) {
    elements.viewsSlider.style.transition =
      "transform 280ms cubic-bezier(0.2, 0.8, 0.2, 1)";
    elements.viewsSlider.classList.remove(
      "view-home",
      "view-saved",
      "view-settings",
    );
    elements.viewsSlider.classList.add(`view-${viewsState.currentView}`);
    elements.viewsSlider.style.transform = "";
  }

  // Clear saved viewer transform
  if (elements.savedViewer) elements.savedViewer.style.transform = "";
};

/**
 * Check if a horizontal global swipe is currently active
 * @returns {boolean}
 */
export const isSwipeActive = () =>
  viewsState.swipe.active && viewsState.swipe.isHorizontal;

/**
 * Initialize UI module
 */
/**
 * Show confirmation modal
 */
export const showConfirmModal = ({
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
}) => {
  let modal = document.querySelector(".confirm-modal");
  if (!modal) {
    modal = createElement("div", "confirm-modal");
    modal.innerHTML = `
            <div class="confirm-card">
                <h2></h2>
                <p></p>
                <div class="confirm-actions">
                    <button class="confirm-btn cancel"></button>
                    <button class="confirm-btn danger"></button>
                </div>
            </div>
        `;
    document.body.appendChild(modal);
  }

  const h2 = modal.querySelector("h2");
  const p = modal.querySelector("p");
  const cancelBtn = modal.querySelector(".cancel");
  const confirmBtn = modal.querySelector(".danger");

  h2.textContent = title;
  p.textContent = message;
  cancelBtn.textContent = cancelText;
  confirmBtn.textContent = confirmText;

  const close = () => {
    modal.classList.remove("active");
  };

  const newCancel = cancelBtn.cloneNode(true);
  const newConfirm = confirmBtn.cloneNode(true);
  cancelBtn.parentNode.replaceChild(newCancel, cancelBtn);
  confirmBtn.parentNode.replaceChild(newConfirm, confirmBtn);

  newCancel.addEventListener("click", close);
  newConfirm.addEventListener("click", () => {
    close();
    if (onConfirm) onConfirm();
  });

  // Force reflow
  modal.offsetHeight;
  modal.classList.add("active");
};

export const initUI = () => {
  initFooterIndicator();
  updateMuteIcon();
  updateFullscreenButton();

  // Set initial active button
  elements.homeBtn?.classList.add("active");
  updateUIVisibility();

  // Hide fullscreen button if not supported
  if (!isFullscreenSupported() && elements.fsBtn) {
    elements.fsBtn.style.display = "none";
  }

  // Listen for fullscreen changes
  document.addEventListener("fullscreenchange", updateFullscreenButton);

  // Prevent clicks after swiping
  document.addEventListener(
    "click",
    (ev) => {
      if (viewsState.swipe.didSwipe) {
        ev.preventDefault();
        ev.stopPropagation();
      }
    },
    { capture: true },
  );
};
